import { useEffect, useRef, useState } from "react";

type Window = {
  weekday: number;
  start_minute: number;
  end_minute: number;
  [key: string]: unknown;
};
type Commitment = {
  market: string;
  minimum_side_notional_usd: string;
  minimum_option_strikes_per_expiry?: number;
  utc_windows: Window[];
  [key: string]: unknown;
};
const markets = ["BTC-PERP", "ETH-PERP", "BTC-OPTIONS", "ETH-OPTIONS"];
const days = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
export function parseClock(value: string): number | undefined {
  if (!/^(?:[01]\d|2[0-3]):[0-5]\d$|^24:00$/.test(value)) return undefined;
  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
}
function Clock({
  value,
  label,
  onChange,
  onInvalidate,
}: {
  value: number;
  label: string;
  onChange: (v: number) => void;
  onInvalidate: () => void;
}) {
  const display = (v: number) =>
    `${String(Math.floor(v / 60)).padStart(2, "0")}:${String(v % 60).padStart(
      2,
      "0"
    )}`;
  const [text, setText] = useState(display(value));
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setText(display(value));
    inputRef.current?.setCustomValidity("");
  }, [value]);
  return (
    <label htmlFor={label.replace(/[^a-z0-9]/gi, "-")}>
      {label.includes("start") ? "Start (UTC)" : "End (UTC)"}
      <input
        ref={inputRef}
        id={label.replace(/[^a-z0-9]/gi, "-")}
        aria-label={label}
        required
        placeholder="09:00"
        value={text}
        pattern="([01][0-9]|2[0-3]):[0-5][0-9]|24:00"
        onChange={(event) => {
          const next = event.target.value;
          onInvalidate();
          setText(next);
          const minute = parseClock(next);
          event.target.setCustomValidity(
            minute === undefined ? "Use HH:MM from 00:00 to 24:00." : ""
          );
          if (minute !== undefined) onChange(minute);
        }}
      />
    </label>
  );
}
function schedulePreset(windows: Window[]) {
  if (!windows.length) return "";
  const exactDays = (expected: number[]) =>
    windows.length === expected.length &&
    expected.every(
      (day) =>
        windows.filter(
          (w) =>
            w.weekday === day && w.start_minute === 0 && w.end_minute === 1440
        ).length === 1
    );
  if (exactDays([0, 1, 2, 3, 4, 5, 6])) return "all";
  if (exactDays([1, 2, 3, 4, 5])) return "weekdays";
  return "custom";
}
function ScheduleEditor({
  windows,
  index,
  onChange,
  onInvalidate,
}: {
  windows: Window[];
  index: number;
  onChange: (windows: Window[]) => void;
  onInvalidate: () => void;
}) {
  const [custom, setCustom] = useState(false);
  const [generation, setGeneration] = useState(0);
  const preset = schedulePreset(windows);
  const showWindows = custom || preset === "custom";
  return (
    <div className="maker-quote-schedule">
      <label htmlFor={`quote-market-${index}-schedule`}>
        Quoting hours (UTC)
        <select
          id={`quote-market-${index}-schedule`}
          aria-label={`Market ${index + 1} quoting hours`}
          required
          value={custom ? "custom" : preset}
          onChange={(event) => {
            onInvalidate();
            const next = event.target.value;
            setCustom(next === "custom");
            setGeneration((n) => n + 1);
            if (next === "custom") {
              if (!windows.length)
                onChange([{ weekday: 1, start_minute: 0, end_minute: 1440 }]);
              return;
            }
            const weekdays =
              next === "all" ? [0, 1, 2, 3, 4, 5, 6] : [1, 2, 3, 4, 5];
            onChange(
              weekdays.map((weekday) => ({
                // Retain any saved extension fields for a matching day.
                ...windows.find((window) => window.weekday === weekday),
                weekday,
                start_minute: 0,
                end_minute: 1440,
              }))
            );
          }}
        >
          <option value="" disabled>
            Choose the agreed schedule
          </option>
          <option value="all">24/7 · every day</option>
          <option value="weekdays">Weekdays · all day Monday–Friday</option>
          <option value="custom">Custom hours</option>
        </select>
      </label>
      <p className="maker-subtle">
        Use the maker’s agreed hours. All times are UTC.
      </p>
      {showWindows && (
        <>
          <p className="maker-subtle">
            24:00 is midnight at the end of the day. Split overnight hours into
            two windows, e.g. Monday 20:00–24:00 and Tuesday 00:00–08:00.
          </p>
          {windows.map((window, windowIndex) => {
            const change = (fields: Partial<Window>) =>
              onChange(
                windows.map((existing, i) =>
                  i === windowIndex ? { ...existing, ...fields } : existing
                )
              );
            const prefix = `Market ${index + 1} window ${windowIndex + 1}`;
            return (
              <div
                className="maker-schedule-row"
                key={`${generation}:${windowIndex}`}
              >
                <label
                  htmlFor={`quote-market-${index}-window-${windowIndex}-day`}
                >
                  Day
                  <select
                    id={`quote-market-${index}-window-${windowIndex}-day`}
                    aria-label={`${prefix} day`}
                    value={window.weekday}
                    onChange={(event) =>
                      change({ weekday: Number(event.target.value) })
                    }
                  >
                    {days.map((day, dayIndex) => (
                      <option key={day} value={dayIndex}>
                        {day}
                      </option>
                    ))}
                  </select>
                </label>
                <Clock
                  onInvalidate={onInvalidate}
                  label={`${prefix} start (UTC)`}
                  value={window.start_minute}
                  onChange={(minute) => change({ start_minute: minute })}
                />
                <Clock
                  onInvalidate={onInvalidate}
                  label={`${prefix} end (UTC)`}
                  value={window.end_minute}
                  onChange={(minute) => change({ end_minute: minute })}
                />
                <button
                  type="button"
                  onClick={() => {
                    setGeneration((n) => n + 1);
                    onChange(windows.filter((_, i) => i !== windowIndex));
                  }}
                >
                  Remove window
                </button>
              </div>
            );
          })}
          <button
            type="button"
            onClick={() =>
              onChange([
                ...windows,
                { weekday: 1, start_minute: 0, end_minute: 1440 },
              ])
            }
          >
            Add window
          </button>
        </>
      )}
    </div>
  );
}
export function QuoteCommitmentEditor({
  value,
  onChange,
  onInvalidate = () => {},
}: {
  value: string;
  onChange: (v: string) => void;
  onInvalidate?: () => void;
}) {
  let rows: Commitment[] | undefined;
  try {
    const parsed = JSON.parse(value);
    if (
      Array.isArray(parsed) &&
      parsed.every(
        (row) =>
          row &&
          typeof row === "object" &&
          typeof row.market === "string" &&
          typeof row.minimum_side_notional_usd === "string" &&
          Array.isArray(row.utc_windows) &&
          row.utc_windows.every(
            (window: Window) =>
              window &&
              [window.weekday, window.start_minute, window.end_minute].every(
                Number.isInteger
              )
          )
      )
    )
      rows = parsed;
  } catch {
    /* Raw input stays untouched until corrected. */
  }
  const write = (next: Commitment[]) => {
    onInvalidate();
    onChange(JSON.stringify(next, null, 2));
  };
  const patch = (index: number, fields: Partial<Commitment>) =>
    write(rows!.map((row, i) => (i === index ? { ...row, ...fields } : row)));
  return (
    <div className="maker-form-wide">
      <h3>3. What should they quote?</h3>
      <p className="maker-subtle">
        Add each agreed market, then set its quote size and hours. Suggested
        amounts are starting points—confirm them with the maker.
      </p>
      {rows === undefined ? (
        <p role="alert">
          Quote configuration needs correction. Open Advanced to fix the saved
          JSON; no fields have been changed.
        </p>
      ) : (
        <>
          {rows.map((row, index) => {
            const options = row.market.endsWith("-OPTIONS");
            return (
              <fieldset
                className="maker-quote-market"
                key={`${index}:${row.market}`}
              >
                <legend>{row.market || `Market ${index + 1}`}</legend>
                <div className="maker-form-grid">
                  <label htmlFor={`quote-market-${index}-name`}>
                    Market
                    <select
                      id={`quote-market-${index}-name`}
                      aria-label={`Market ${index + 1} name`}
                      value={row.market}
                      required
                      onChange={(event) => {
                        const market = event.target.value;
                        patch(index, {
                          market,
                          ...(market.endsWith("-PERP")
                            ? { minimum_option_strikes_per_expiry: undefined }
                            : {}),
                        });
                      }}
                    >
                      <option value="" disabled>
                        Choose a market
                      </option>
                      {row.market && !markets.includes(row.market) && (
                        <option value={row.market}>
                          {row.market} (saved market)
                        </option>
                      )}
                      {markets.map((market) => (
                        <option
                          key={market}
                          value={market}
                          disabled={rows!.some(
                            (other, i) => i !== index && other.market === market
                          )}
                        >
                          {market
                            .replace("-PERP", " perpetuals")
                            .replace("-OPTIONS", " options")}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="maker-quote-size">
                    <label htmlFor={`quote-market-${index}-minimum`}>
                      Minimum on each side (USD)
                      <input
                        id={`quote-market-${index}-minimum`}
                        aria-label={`Market ${index + 1} minimum per side`}
                        aria-describedby={`quote-market-${index}-size-help`}
                        value={row.minimum_side_notional_usd}
                        required
                        inputMode="decimal"
                        pattern="[0-9]+(\.[0-9]+)?"
                        placeholder={options ? "e.g. 5000" : "e.g. 50000"}
                        onChange={(event) =>
                          patch(index, {
                            minimum_side_notional_usd: event.target.value,
                          })
                        }
                      />
                    </label>
                    <p
                      className="maker-subtle"
                      id={`quote-market-${index}-size-help`}
                    >
                      {options
                        ? "Underlying notional on both bid and ask, for each option contract. This is not the option premium."
                        : "Quote at least this USD value on both the bid and the ask."}
                    </p>
                    {markets.includes(row.market) && (
                      <button
                        type="button"
                        onClick={() =>
                          patch(index, {
                            minimum_side_notional_usd: options
                              ? "5000"
                              : "50000",
                          })
                        }
                      >
                        Use suggested ${options ? "5,000" : "50,000"}
                      </button>
                    )}
                  </div>
                  {(options ||
                    row.minimum_option_strikes_per_expiry !== undefined) && (
                    <div>
                      <label htmlFor={`quote-market-${index}-strikes`}>
                        Calls and puts per expiry
                        <input
                          id={`quote-market-${index}-strikes`}
                          aria-label={`Market ${index + 1} strikes per expiry`}
                          type="number"
                          min={1}
                          max={5}
                          required
                          value={row.minimum_option_strikes_per_expiry ?? ""}
                          placeholder="e.g. 2"
                          onChange={(event) =>
                            patch(index, {
                              minimum_option_strikes_per_expiry: Number(
                                event.target.value
                              ),
                            })
                          }
                        />
                      </label>
                      <p className="maker-subtle">
                        2 means 2 calls and 2 puts among the 5 eligible strikes,
                        each with a bid and ask.
                      </p>
                    </div>
                  )}
                </div>
                <ScheduleEditor
                  windows={row.utc_windows}
                  index={index}
                  onChange={(utc_windows) => patch(index, { utc_windows })}
                  onInvalidate={onInvalidate}
                />
                <div className="maker-form-actions maker-quote-actions">
                  <button
                    type="button"
                    onClick={() => write(rows!.filter((_, i) => i !== index))}
                  >
                    Remove market
                  </button>
                </div>
              </fieldset>
            );
          })}
          <button
            type="button"
            onClick={() =>
              write([
                ...rows!,
                { market: "", minimum_side_notional_usd: "", utc_windows: [] },
              ])
            }
          >
            Add market
          </button>
        </>
      )}
      <details
        className="maker-advanced"
        open={rows === undefined || undefined}
      >
        <summary>Advanced: quote configuration JSON</summary>
        <label htmlFor="maker-approved-quote-commitments">
          Approved quote commitments (JSON)
          <textarea
            id="maker-approved-quote-commitments"
            rows={8}
            value={value}
            onChange={(event) => {
              onInvalidate();
              onChange(event.target.value);
            }}
          />
        </label>
      </details>
    </div>
  );
}
